import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuBookOpen, LuCompass, LuHistory, LuPlus } from 'react-icons/lu';
import AuthContext from '../../components/shared/AuthContext';
import PageMeta from '../../components/seo/PageMeta';
import LearnPageShell from './LearnPageShell';
import LibraryReadListPanel from '../../components/library/LibraryReadListPanel';
import LearnMyNotebooksPanel from '../../components/learn/LearnMyNotebooksPanel';
import LearnHubImportQueue from './LearnHubImportQueue';
import '../../components/library/LibraryReadListPanel.css';
import { LEARN_HUB } from '../../config/appLabels';
import '../../components/hub/hub-guide.css';
import HubPlaybook from '../../components/hub/HubPlaybook';
import { LEARN_HUB_PLAYBOOK } from '../../config/hubPlaybookContent';
import { computeLearnHubStats } from './learnHubStats';
import { resolveAuthUser } from '../../utils/notebookOwnership';
import './learn.css';

export default function LearnPage() {
  const { apiClient, user: authUser } = useContext(AuthContext);
  const userProfile = useMemo(() => resolveAuthUser(authUser), [authUser]);
  const navigate = useNavigate();
  const [notebooks, setNotebooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const mine = await apiClient.get('/api/v1/learn/notebooks');
      setNotebooks(Array.isArray(mine.data) ? mine.data : []);
    } catch {
      setError('Could not load your Q&A. Verify the API is running.');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const hash = window.location.hash;
    if (loading || hash !== '#learn-library') return;
    const id = hash.slice(1);
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(t);
  }, [loading]);

  const stats = useMemo(() => computeLearnHubStats(notebooks, userProfile), [notebooks, userProfile]);

  const filteredNotebooks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return notebooks;
    return notebooks.filter((nb) => {
      const title = (nb.title || '').toLowerCase();
      const mod = (nb.moduleLabel || '').toLowerCase();
      const desc = (nb.description || '').toLowerCase();
      return title.includes(q) || mod.includes(q) || desc.includes(q);
    });
  }, [notebooks, query]);

  const showSearch = !loading && notebooks.length > 3;

  return (
    <>
      <PageMeta title={LEARN_HUB.name} description={LEARN_HUB.tagline} />
      <LearnPageShell userProfile={userProfile} loading={loading}>
        <LearnHubImportQueue apiClient={apiClient} />

        <section className="learn-hero-panel learn-hero-panel--compact" aria-labelledby="learn-hub-title">
          <header className="learn-hero-panel__head learn-hub-page-hero">
            <div className="learn-hub-page-hero__top">
              <div className="learn-hub-page-hero__brand">
                <span className="learn-hub-page-hero__icon" aria-hidden>
                  <LuBookOpen />
                </span>
                <div className="learn-hub-page-hero__titles">
                  <h1 id="learn-hub-title" className="learn-hub-page-hero__title">
                    {LEARN_HUB.eyebrow}
                  </h1>
                  <p className="learn-hub-page-hero__subtitle">{LEARN_HUB.summary}</p>
                </div>
              </div>
              <div className="learn-hub-page-hero__actions">
                <button
                  type="button"
                  className="home-btn home-btn-primary learn-btn-with-icon"
                  onClick={() => navigate('/hub/notebooks/new')}
                >
                  <LuPlus aria-hidden />
                  Create Q&amp;A
                </button>
                <Link to="#learn-library" className="home-btn home-btn-secondary">Browse catalog</Link>
              </div>
            </div>
            <div className="catalog-showcase-lead-row learn-hub-page-hero__lead-row">
              <p className="catalog-showcase-lead learn-hub-page-hero__lead">{LEARN_HUB.tagline}</p>
              <ol className="catalog-showcase-flow catalog-showcase-flow--inline learn-hub-page-hero__flow" aria-label="Q&A hub flow">
                <li><span>Create</span> Q&amp;A</li>
                <li><span>Import</span> PDFs &amp; URLs</li>
                <li><span>Practice</span> on Study hub</li>
              </ol>
            </div>
          </header>

          <div className="learn-hero-panel__body">
            <div className="learn-hero-panel__block learn-hero-panel__block--flush">
              <HubPlaybook {...LEARN_HUB_PLAYBOOK} journeyHighlight="learn" />
            </div>

            <div className="learn-hero-panel__block learn-hero-panel__block--shortcuts">
              <div className="learn-hero-shortcuts-row">
                <div className="learn-hero-shortcuts-row__links">
                  <p className="hub-section-label">Shortcuts</p>
                  <nav className="learn-page-links learn-hero-panel__shortcuts" aria-label="Learn shortcuts">
                    <Link to="/hub/today" className="learn-page-link">
                      <LuCompass aria-hidden />
                      Today&apos;s plan
                    </Link>
                    <Link to="/mastery" className="learn-page-link">
                      <LuBookOpen aria-hidden />
                      Knowledge map
                    </Link>
                    <Link to="/hub/imports" className="learn-page-link">
                      <LuHistory aria-hidden />
                      Import history
                    </Link>
                    <Link to="/dashboard" className="learn-page-link">
                      Study hub
                    </Link>
                  </nav>
                </div>
                {!loading && (
                  <dl
                    className="learn-page-stats learn-page-stats--hero learn-hero-shortcuts-row__stats"
                    aria-label="Your Q&A metrics"
                  >
                    <div>
                      <dt>Yours</dt>
                      <dd>{stats.createdByYou}</dd>
                    </div>
                    <div>
                      <dt>Questions</dt>
                      <dd>{stats.totalQuestions}</dd>
                    </div>
                    <div>
                      <dt>Studying</dt>
                      <dd>{stats.inProgress}</dd>
                    </div>
                    <div>
                      <dt>Avg read</dt>
                      <dd>{stats.avgProgress}%</dd>
                    </div>
                  </dl>
                )}
              </div>
            </div>
          </div>
        </section>

        <LearnMyNotebooksPanel
          loading={loading}
          notebooks={notebooks}
          filteredNotebooks={filteredNotebooks}
          query={query}
          onQueryChange={setQuery}
          stats={stats}
          showSearch={showSearch}
          onCreateClick={() => navigate('/hub/notebooks/new')}
        />

        <LibraryReadListPanel />

        {error && <p className="learn-inline-alert" role="alert">{error}</p>}

        <p className="learn-footnote">
          Only upload material you have the right to use. QnaRead does not verify document ownership.
        </p>
      </LearnPageShell>
    </>
  );
}
